import React, { memo, useState } from 'react';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';
import {
	isNullOrUndefined,
	isNumber,
	isObject,
	isString,
	useDeepMemoize,
	useObserveRef,
} from '@chkp/mwc-common';
import styles from './Cell.scss';

// ------------------------------------------------
// Detect when cell is hovered and pass 'hover' prop to cellRenderer
// Note: direct onMouseEnter & onMouseLeave is unreliable -
// https://github.com/facebook/react/issues/4492
//
const useCellHover = (hoverObservable, rowKey, columnKey) => {
	const [hover, setHover] = useState(false);
	useObserveRef(hoverObservable, (details) =>
		setHover(
			details && details.rowKey === rowKey && details.columnKey === columnKey,
		),
	);

	return hover;
};
// ------------------------------------------------

/**
 * Resolve cell-context from row-context and column
 * @param cellProps
 * @returns {Object}
 */
const useCellContext = (cellProps) => {
	const { column, rowContext } = cellProps;
	const {
		record,
		context,
		isScrolling,
		hoverObservable,
		key: rowKey,
	} = rowContext;
	const { renderer, key: columnKey, padding } = column;
	const value = (isObject(record) && record[column.key]) || '';

	const hover = useCellHover(hoverObservable, rowKey, columnKey);
	const cellContext = {
		value,
		record,
		column,
		context,
		border: rowContext.cellBorders,
		row: rowContext,
		isScrolling,
		hover,
		padding,
	};

	try {
		const mod = renderer?.(cellContext);
		if (React.isValidElement(mod) || isString(mod) || isNumber(mod)) {
			cellContext.content = mod;
		} else if (isObject(mod)) {
			Object.assign(cellContext, mod);
		}
	} catch (e) {
		console.error('Cell renderer error: ', cellContext, e);
		cellContext.content = '';
	}

	return useDeepMemoize(cellContext);
};

let Cell = (props) => {
	const cellContext = useCellContext(props);
	const {
		content,
		value,
		row,
		column,
		border,
		style,
		className,
		wrapper,
		padding,
	} = cellContext;

	const {
		selected: rowSelected,
		cellStyle: rowCellStyle,
		cellClassName: rowCellClassName,
		indent: rowIndent,
		height: rowHeight,
	} = row;

	const { locked, idx: columnIdx, key: columnKey } = column;
	const compensateRowIndent = (columnIdx === 0 && rowIndent) || 0;

	// the <span> wrapping is required for controlling cell's content style, e.g. .disabledCell>*{opacity:.5}
	const cellContent = isNullOrUndefined(content) ? (
		<span>{value}</span>
	) : (
		content
	);

	// column / renderer can set padding=false or padding='..css padding'
	const paddingStyle =
		padding === false
			? { padding: 0 }
			: isString(padding)
			? { padding }
			: undefined;

	let ret = (
		<div
			role='cell'
			data-column-key={columnKey}
			className={clsx(
				styles.Cell,
				border && styles.withBorder,
				rowSelected && styles.rowSelected,
				locked && styles.locked,
				rowCellClassName,
				className,
			)}
			style={{
				width: column.finalWidth - compensateRowIndent,
				height: rowHeight - 1, // Account for border. // Note: Height must be provided, otherwise there's some glitch on chrome & CellDropdown
				...paddingStyle,
				...rowCellStyle,
				...style,
			}}
		>
			{cellContent}
		</div>
	);

	// Let renderer to wrap or manipulate cell element
	if (wrapper) {
		ret = wrapper(ret);
	}

	return ret;
};

Cell = memo(Cell);

Cell.propTypes = {
	/* eslint-disable react/no-unused-prop-types */
	column: PropTypes.object,
	rowContext: PropTypes.object,
};

export { Cell };
